// @ts-nocheck -- RARE-264 mechanical rename; type this file under RARE-265
import { gql } from "@apollo/client";
import { setAuthToken } from "../auth";

/**
 * Single-flight monitor-JWT refresh.
 *
 * The monitor refresh cookie is httpOnly, same-site, and shared across
 * every tab on the origin. The access token, by contrast, lives in
 * per-tab sessionStorage (see src/auth.js). This helper is the bridge
 * between the two: given an Apollo client, it fires `RefreshMonitorToken`
 * with no `Authorization` header (the cookie is the only credential the
 * server needs), stores the returned JWT via `setAuthToken`, and returns
 * it to the caller.
 *
 * The empty `authorization` on the operation context below is only half
 * of what makes that true. `createAuthLink` runs ahead of this call and
 * used to merge the stale JWT straight over the top of it; the header
 * is stripped there, keyed on the operation name. See
 * graphql/auth-link.js.
 *
 * Two callers rely on it:
 *
 *   1. The Apollo `errorLink` in `src/index.js`, which uses it to
 *      recover from a 401 mid-session.
 *   2. `AuthProvider`'s bootstrap effect in `src/contexts/auth-context.js`,
 *      which uses it to rehydrate a new tab from the shared refresh
 *      cookie before deciding whether the user is signed out.
 *
 * A shared module-level `inFlight` promise makes the call single-flight
 * across both paths: rotating the same refresh cookie twice in parallel
 * would trip the server's replay-detection and force a full sign-out.
 */

export const REFRESH_MUTATION = gql`
  mutation RefreshMonitorToken {
    RefreshMonitorToken {
      jwt
      accessTtl
    }
  }
`;

// monitor-api's verdicts on the refresh COOKIE. These are the only
// answers that mean the user must re-authenticate: the server looked
// at the cookie and rejected it. See monitor-api
// src/api/auth-exchange.api.ts, which is the only place either code is
// raised.
export const REFRESH_FAILURE_CODES = new Set(["E_UNAUTHENTICATED", "E_REFRESH_REPLAYED"]);

export const isRefreshFailure = (graphQLErrors) => {
  if (!Array.isArray(graphQLErrors)) return false;
  return graphQLErrors.some((e) => REFRESH_FAILURE_CODES.has(e?.extensions?.code));
};

/**
 * Refresh failures come in two flavours and callers must be able to
 * tell them apart. Before MONITOR-135 every failure rejected with a
 * bare `Error`, so the errorLink treated a network blip exactly like a
 * replayed refresh cookie and signed the user out either way.
 *
 *   terminal: true  -> the cookie is dead. The user must re-authenticate.
 *   terminal: false -> transport-level; retrying may well work.
 */
export class RefreshError extends Error {
  constructor(message, { terminal = false, cause } = {}) {
    super(message);
    this.name = "RefreshError";
    this.terminal = terminal;
    if (cause !== undefined) this.cause = cause;
  }
}

/**
 * True only for failures the user cannot be recovered from without a
 * fresh sign-in. Anything unrecognised is treated as recoverable: a
 * needless retry costs one round trip, a needless sign-out costs the
 * user their page.
 */
export const isTerminalRefreshFailure = (error) => error?.terminal === true;

let inFlight = null;

/**
 * Kick off (or join) a refresh round-trip. Resolves with the fresh
 * monitor JWT on success.
 *
 * Always rejects with a `RefreshError`. Check `isTerminalRefreshFailure`
 * before deciding to sign the user out: a terminal rejection means the
 * refresh cookie is dead, anything else is worth retrying.
 *
 * The `client` is the Apollo client to fire the mutation through: it
 * is passed in rather than imported so tests (and the errorLink,
 * which gets it from `operation.getContext().client`) can supply
 * their own.
 */
export const refreshMonitorToken = (client) => {
  if (!inFlight) {
    inFlight = (async () => {
      const result = await client.mutate({
        mutation: REFRESH_MUTATION,
        // The refresh cookie is the only credential the server needs.
        // Sending a stale Authorization header would just log noise.
        context: { headers: { authorization: "" } },
        errorPolicy: "all",
      });
      if (result.errors && result.errors.length > 0) {
        if (isRefreshFailure(result.errors)) {
          throw new RefreshError("E_REFRESH_FAILED", { terminal: true });
        }
        // Anything else is not a verdict on the cookie. In particular a
        // bare `http: 401` used to be read as one, which is wrong: the
        // gateway raises 401 INVALID_TOKEN / AUTH_REQUIRED from a
        // preHandler, on the strength of the Authorization header
        // alone, before monitor-api is reached at all. See MONITOR-182.
        throw new RefreshError(result.errors[0]?.message ?? "Refresh failed", {
          terminal: false,
        });
      }
      const jwt = result.data?.RefreshMonitorToken?.jwt;
      // The server answered without error but gave us nothing usable.
      // Recoverable: this is far more likely a truncated / proxied
      // response than a revoked cookie.
      if (!jwt) {
        throw new RefreshError("Refresh response carried no JWT", { terminal: false });
      }
      setAuthToken(jwt);
      return jwt;
    })()
      .catch((error) => {
        // Normalise everything that escapes into a RefreshError so
        // callers only ever have to ask `isTerminalRefreshFailure`.
        if (error instanceof RefreshError) throw error;
        // Transport-level failures are never terminal, 401 included.
        // monitor-api answers a dead cookie in the GraphQL body, with
        // E_UNAUTHENTICATED or E_REFRESH_REPLAYED, handled above. A raw
        // 401 on this operation therefore came from the gateway judging
        // the Authorization header, which says nothing about the cookie.
        // Reading it as terminal is what turned MONITOR-182's stale
        // bearer into an instant sign-out rather than a retry.
        throw new RefreshError(error?.message ?? "Refresh failed", {
          terminal: false,
          cause: error,
        });
      })
      .finally(() => {
        // Reset AFTER the promise settles so a second caller arriving
        // during this call joins the in-flight attempt rather than
        // firing its own.
        inFlight = null;
      });
  }
  return inFlight;
};

// Exposed for tests only. Resets the single-flight guard between
// runs; production code should never need this.
export const __resetRefreshInFlightForTests = () => {
  inFlight = null;
};
